import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useLoginForm } from '../hooks/useLoginForm';
import Brand from './Brand';

const Spinner = () => <span className="login-spinner" aria-hidden="true" />;

const FieldError = ({ message }) => (message ? <p className="login-field-error">{message}</p> : null);

const EmailField = ({ value, error, disabled, onChange }) => (
  <div className="login-field">
    <label htmlFor="email" className="login-label">Email</label>
    <div className="login-input-wrap">
      <input
        type="email"
        id="email"
        name="email"
        value={value}
        onChange={onChange}
        className={`login-input ${error ? 'has-error' : ''}`}
        placeholder="you@example.com"
        disabled={disabled}
        autoComplete="email"
      />
    </div>
    <FieldError message={error} />
  </div>
);

const PasswordField = ({ value, error, disabled, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="login-field">
      <label htmlFor="password" className="login-label">Password</label>
      <div className="login-input-wrap">
        <input
          type={visible ? 'text' : 'password'}
          id="password"
          name="password"
          value={value}
          onChange={onChange}
          className={`login-input has-suffix ${error ? 'has-error' : ''}`}
          placeholder="Enter your password"
          disabled={disabled}
          autoComplete="current-password"
        />
        <button
          type="button"
          className="login-input-suffix"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeSlashIcon /> : <EyeIcon />}
        </button>
      </div>
      <FieldError message={error} />
    </div>
  );
};

/** The sign-in card: email/password form plus the Diplo SSO button. */
export default function LoginForm() {
  const { form, fieldErrors, isSubmitting, loginError, ssoLoading, handleChange, handleSubmit, handleSsoLogin } = useLoginForm();

  return (
    <div className="login-card">
      <div className="login-mobile-brand">
        <Brand />
      </div>

      <h1 className="login-title">Sign in</h1>
      <p className="login-sub">
        Welcome back. Use your email and password, or continue with Diplo SSO.
      </p>

      {loginError && (
        <div className="login-alert" role="alert">
          <ExclamationTriangleIcon />
          <span>{loginError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="login-form" noValidate>
        <EmailField value={form.email} error={fieldErrors.email} disabled={isSubmitting} onChange={handleChange} />
        <PasswordField value={form.password} error={fieldErrors.password} disabled={isSubmitting} onChange={handleChange} />
        <button type="submit" disabled={isSubmitting} className="login-btn login-btn-primary">
          {isSubmitting ? <><Spinner />Signing in...</> : 'Sign in'}
        </button>
      </form>

      <div className="login-divider">or</div>

      <button type="button" onClick={handleSsoLogin} disabled={ssoLoading} className="login-btn login-btn-secondary">
        {ssoLoading ? (
          <><Spinner />Connecting to SSO...</>
        ) : (
          <><ShieldCheckIcon style={{ width: 16, height: 16 }} />Continue with Diplo SSO</>
        )}
      </button>

      <div className="login-foot">
        Don't have an account? Contact your administrator.
      </div>
    </div>
  );
}
